import { useMemo } from "react";
import { Chip, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getAllRows } from "../../../../../../common/FiltersFormEngine/querySelectors";
import { DashboardAdvancedModeModal } from "../../../../../../components/common/Dashboard/components/DashboardAdvancedModeDialog";
import { DashboardFilterItem } from "../../../../../../components/common/Dashboard/components/DashboardFilterItem";
import { FilterContexType } from "../../../../../../components/layout/ViewHeader";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import { getRootCauseDashboardFilters } from "../../../../../../context/FiltersProvider/selectors/RootCause/dashboardSelectors";
import { DashboardFilterInstance } from "../../../../../../context/FiltersProvider/types";
import { DashboardAccordion } from "./components/DashboardAccordion";
import { EmptyDashboard } from "./components/EmptyDashboard";
import { RootCauseAdvancedFilters } from "./components/RootCauseAdvancedFilters";

interface Props {
	cardType: "perimeter" | "baseline" | "comparison";
	filterContext: FilterContexType;
}

export const CardDashboard = ({ cardType, filterContext }: Props) => {
	const { t } = useTranslation(["dataInsights.rootcause", "common"]);

	const {
		registry,
		rootCauseState,
		removeRootCausePerimeterFilter,
		updateRootCauseDashboardFilter,
		removeRootCauseDashboardFilter,
		clearRootCausePerimeter,
		clearRootCauseWorkspace,
		rootCauseAdvancedModeModal,
		openRootCauseSwitchAdvancedModeModal,
		closeRootCauseAdvancedModeModal,
		confirmRootCauseAdvancedMode,
	} = useFiltersRegistry();

	const workspace = cardType === "baseline" ? rootCauseState.baseline : rootCauseState.comparison;

	const filters = useMemo(
		() => getRootCauseDashboardFilters(registry, workspace.dashboardFilters),
		[registry, workspace.dashboardFilters]
	);

	const advancedFiltersCount = useMemo(() => {
		return getAllRows(workspace.advancedForm).length;
	}, [workspace.advancedForm]);

	if (cardType === "perimeter") {
		return rootCauseState.perimeter.length > 0 ? (
			<DashboardAccordion
				title={t("cardSubTitles/criteria")}
				count={rootCauseState.perimeter.length}
				onClearDashboard={clearRootCausePerimeter}
			>
				<Stack direction="row" spacing={2} flexWrap="wrap">
					{rootCauseState.perimeter.map((filter) => (
						<Chip
							key={filter.id}
							label={filter.label}
							onDelete={() => removeRootCausePerimeterFilter(filter.id)}
						/>
					))}
				</Stack>
			</DashboardAccordion>
		) : (
			<EmptyDashboard cardType={cardType} />
		);
	}

	const workspaceType: "baseline" | "comparison" = cardType === "baseline" ? "baseline" : "comparison";

	const handleUpdateFilter = (id: string, patch: Partial<DashboardFilterInstance>) => {
		updateRootCauseDashboardFilter(workspaceType, id, patch);
	};

	const handleRemoveFilter = (id: string) => {
		removeRootCauseDashboardFilter(workspaceType, id);
	};

	const onSwitchChange = () => openRootCauseSwitchAdvancedModeModal(workspaceType);

	if (!filters.length && !workspace.advancedMode) {
		return <EmptyDashboard cardType={cardType} />;
	}

	const isAdvancedModalOpen =
		rootCauseAdvancedModeModal.open && rootCauseAdvancedModeModal.workspace === workspaceType;

	const isConfigurationOverride = Boolean(rootCauseAdvancedModeModal.filter);

	return (
		<>
			<DashboardAccordion
				title={t("cardSubTitles/condition")}
				advancedMode={workspace.advancedMode}
				onSwitchChange={onSwitchChange}
				count={workspace.advancedMode ? advancedFiltersCount : filters.length}
				onClearDashboard={() => clearRootCauseWorkspace(workspaceType)}
			>
				{!workspace.advancedMode && (
					<Stack direction="row" flexWrap="wrap" gap={2}>
						{filters.map(({ instance, filter }) => (
							<DashboardFilterItem
								key={instance.id}
								instance={instance}
								filter={filter}
								onUpdate={handleUpdateFilter}
								onRemove={handleRemoveFilter}
							/>
						))}
					</Stack>
				)}

				{workspace.advancedMode && (
					<RootCauseAdvancedFilters workspace={workspaceType} filterContext={filterContext} />
				)}
			</DashboardAccordion>

			<DashboardAdvancedModeModal
				title={
					isConfigurationOverride
						? t("common:dashboard/title add filter", {
								filterName: rootCauseAdvancedModeModal.filter?.name,
							})
						: t("common:dashboard/title")
				}
				paragraphs={
					isConfigurationOverride
						? (t("common:dashboard/list add filter", {
								filterName: rootCauseAdvancedModeModal.filter?.name,
								returnObjects: true,
							}) as string[])
						: (t("common:dashboard/list", { returnObjects: true }) as string[])
				}
				confirmText={t("common:dashboard/confirm")}
				open={isAdvancedModalOpen}
				onClose={closeRootCauseAdvancedModeModal}
				onConfirm={confirmRootCauseAdvancedMode}
			/>
		</>
	);
};
